import { Line, Bar } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import { ThemeType } from '../../../theme/themeType';
import S from './ScoreTrendStyles';

type ScoreData = {
  scoreDate: string;
  score: number;
};

export type Props = {
  title: string;
  period: string;
  data?: ScoreData[];
};

const buildChartOptions = (theme: ThemeType): Record<string, any> => ({
  scales: {
    y: {
      beginAtZero: true,
      min: 0,
      max: 100,
      ticks: {
        color: theme.colors.text,
        stepSize: 20,
      },
      grid: {
        color: theme.colors.outline,
      },
      border: {
        display: false,
      },
    },
    x: {
      ticks: {
        color: theme.colors.text,
      },
      grid: {
        display: false,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
  },
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 16,
      left: 4,
      right: 4,
      bottom: 4,
    },
  },
});

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = (date: string, period: string): string => {
  const d = new Date(date);
  if (period === 'daily') {
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: LOCAL_TZ,
    });
  }
  return d.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    timeZone: LOCAL_TZ,
  });
};

function ScoreTrendWidget({ title, data = [], period }: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const CHART_OPTIONS = buildChartOptions(theme);
  const sortedData = [...data].sort((a: ScoreData, b: ScoreData) => (
    new Date(a.scoreDate).getTime() - new Date(b.scoreDate).getTime()
  ));

  const scoreValues = sortedData.map((item: ScoreData) => item.score);

  return (
    <S.Wrapper>
      <div className="title">{title}</div>
      {sortedData.length === 0 ? (
        <div className="score-graph no-data">
          <p>No score data for selected period</p>
        </div>
      ) : (
        <div className="score-graph">
          {sortedData.length === 1 ? (
            <Bar
              data={{
                labels: [formatDate(sortedData[0].scoreDate, period)],
                datasets: [{
                  label: 'Score',
                  data: scoreValues,
                  backgroundColor: theme.colors.salmon,
                  borderRadius: 6,
                  barPercentage: 0.15,
                }],
              }}
              options={CHART_OPTIONS}
            />
          ) : (
            <Line
              data={{
                labels: sortedData.map((item: ScoreData) => formatDate(item.scoreDate, period)),
                datasets: [{
                  label: 'Score',
                  data: scoreValues,
                  borderWidth: 2,
                  borderColor: theme.colors.salmon,
                  pointStyle: 'circle',
                  pointRadius: 3,
                  pointBackgroundColor: theme.colors.salmon,
                  pointBorderWidth: 0,
                  pointHoverRadius: 5,
                }],
              }}
              options={CHART_OPTIONS}
            />
          )}
        </div>
      )}
    </S.Wrapper>
  );
}

export default ScoreTrendWidget;
